import { useCallback, useSyncExternalStore } from 'react'

/**
 * Which notifications this person has read — in this browser, as VentaHub
 * kept them (`readNotifications` in `localStorage`).
 *
 * A per-viewer convenience, not view state, so storage rather than the URL.
 * Keyed by principal so two people sharing a browser do not read each
 * other's bell. One store for the page: the badge and the panel read the
 * same set, and another tab's change arrives through the `storage` event.
 * Storage can be missing or blocked; then reads last for the visit.
 */
export const READS_STORAGE_KEY = 'ventawiz.notifications.read'

/** The most ids kept. Notifications last a fortnight; this is far more. */
const MAX_READS = 500

const listeners = new Set<() => void>()
const memory = new Map<string, readonly string[]>()
const EMPTY: readonly string[] = []

function keyFor(principal: string | undefined): string {
  return principal ? `${READS_STORAGE_KEY}.${principal}` : READS_STORAGE_KEY
}

function load(key: string): readonly string[] {
  const cached = memory.get(key)
  if (cached) return cached
  let ids: readonly string[] = EMPTY
  try {
    const raw = window.localStorage.getItem(key)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    if (Array.isArray(parsed)) ids = parsed.filter((id): id is string => typeof id === 'string')
  } catch {
    // Private mode, blocked storage or a hand-edited value: nothing read yet.
  }
  memory.set(key, ids)
  return ids
}

function save(key: string, ids: readonly string[]) {
  const kept = ids.slice(-MAX_READS)
  memory.set(key, kept)
  try {
    window.localStorage.setItem(key, JSON.stringify(kept))
  } catch {
    // Unpersisted beats unusable: the read state still holds for this visit.
  }
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  const onStorage = (event: StorageEvent) => {
    if (!event.key?.startsWith(READS_STORAGE_KEY)) return
    memory.delete(event.key)
    listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

/** Forget the in-memory copy — for tests, which share one module between cases. */
export function resetNotificationReads() {
  memory.clear()
}

export function useNotificationReads(principal: string | undefined) {
  const key = keyFor(principal)
  const read = useSyncExternalStore(
    subscribe,
    () => load(key),
    () => EMPTY,
  )

  const markRead = useCallback(
    (ids: readonly string[]) => {
      const current = load(key)
      const fresh = ids.filter((id) => !current.includes(id))
      if (fresh.length) save(key, [...current, ...fresh])
    },
    [key],
  )

  const markUnread = useCallback(
    (id: string) => {
      const current = load(key)
      if (current.includes(id))
        save(
          key,
          current.filter((read) => read !== id),
        )
    },
    [key],
  )

  return { read, markRead, markUnread }
}
